/**
 * Builds the active filter "chips" shown above a CustomTable from the flat URL
 * filters. This is the display layer: it parses keys through the grammar defined
 * in filterParams.ts and turns each active filter into a translated, formatted
 * chip. Kept separate from the grammar so display concerns (dayjs, i18n, the
 * chip shape) don't live alongside the URL/view encoding.
 */
import dayjs from 'dayjs';
import { i18nKeys, type TranslateFunction } from 'locales';
import { isNonNullish, isNullish } from 'remeda';
import type { FilterType } from 'types/views';
import {
  BOOKKEEPING_KEYS,
  EMPTY_OPERATOR,
  FILLED_OPERATOR,
  HANDLER_SUFFIX,
  isMinMaxKey,
  MAX_PREFIX,
  MIN_PREFIX,
  OPERATOR_SUFFIX,
  RANGE_END_SUFFIX,
  RANGE_START_SUFFIX,
  rangeFieldName,
  suffixFieldName,
  UNSET_BOUND,
} from './filterGrammar';
import { labelByConstant } from './filterValueLabel';

type DisplayFilter = {
  key: string;
  name: string;
  // string[] for id-carrying fields; the consumer resolves the ids to labels.
  text: string | string[];
  // Deleting one half of a min/max or start/end pair removes the other too.
  pairedKey?: string;
};

type AvailableFilter = {
  name: string;
  name_translated: string;
  filter_type?: FilterType;
  is_api?: boolean;
  constant_name?: string | null;
};

type BuildVisibleFiltersOptions = {
  filters: Record<string, any>;
  availableFilters: AvailableFilter[];
  constants: Record<string, unknown>;
  t: TranslateFunction;
};

type ChipContext = BuildVisibleFiltersOptions;

const rangeBoundText = (rawBound: unknown, format: (bound: string) => string) =>
  rawBound && rawBound !== UNSET_BOUND ? format(rawBound as string) : '∞';

const dateRangeChip = (
  key: string,
  { filters, availableFilters }: ChipContext,
): DisplayFilter | null => {
  const fieldName = suffixFieldName(key);
  const filter = availableFilters.find(_filter => _filter.name === fieldName);
  if (isNullish(filter)) return null;

  const startKey = `${fieldName}${RANGE_START_SUFFIX}`;
  const endKey = `${fieldName}${RANGE_END_SUFFIX}`;
  return {
    key,
    pairedKey: key.includes(RANGE_START_SUFFIX) ? endKey : startKey,
    name: filter.name_translated,
    // <- if a bound is the 'null' sentinel, change it on the backend
    text: `${rangeBoundText(filters[startKey], b => dayjs(b).format('L'))} - ${rangeBoundText(
      filters[endKey],
      b => dayjs(b).format('L'),
    )}`,
  };
};

const minMaxChip = (
  key: string,
  { filters, availableFilters }: ChipContext,
): DisplayFilter | null => {
  const fieldName = rangeFieldName(key);
  const filter = availableFilters.find(_filter => _filter.name === fieldName);
  if (isNullish(filter)) return null;

  const minKey = `${MIN_PREFIX}${fieldName}`;
  const maxKey = `${MAX_PREFIX}${fieldName}`;
  return {
    key,
    pairedKey: key.includes(MIN_PREFIX) ? maxKey : minKey,
    name: filter.name_translated,
    text: `${rangeBoundText(filters[minKey], value => value)} - ${rangeBoundText(filters[maxKey], value => value)}`,
  };
};

const handlerChip = (
  key: string,
  { filters, availableFilters, t }: ChipContext,
): DisplayFilter | null => {
  if (filters[key] !== EMPTY_OPERATOR && filters[key] !== FILLED_OPERATOR) return null;
  const filter = availableFilters.find(_filter => _filter.name === suffixFieldName(key));
  if (isNullish(filter)) return null;

  return {
    key,
    name: filter.name_translated,
    text: `-${t(filters[key] === EMPTY_OPERATOR ? 'EMPTY' : 'FILL')}`,
  };
};

const quickSearchChip = (key: string, { filters, t }: ChipContext): DisplayFilter => ({
  key,
  name: t(i18nKeys.FORM.QUICK_SEARCH.PLACEHOLDER),
  text: filters[key],
});

// Resolve a select/string value to its display text: through the named constant
// (codes -> labels), as an id array for is_api filters, otherwise the raw value.
const valueText = (
  key: string,
  filter: AvailableFilter,
  { filters, constants }: ChipContext,
): string | string[] => {
  const constantValues = filter.constant_name ? constants[filter.constant_name] : undefined;
  if (Array.isArray(constantValues)) {
    const codes = Array.isArray(filters[key]) ? filters[key] : filters[key].split(',');
    return labelByConstant(codes, constantValues);
  }
  return filter.is_api && !Array.isArray(filters[key]) ? filters[key].split(',') : filters[key];
};

const valueChip = (key: string, ctx: ChipContext): DisplayFilter | null => {
  const { filters, availableFilters, t } = ctx;
  const filter = availableFilters.find(_filter => _filter.name === key);
  if (isNullish(filter)) return null;

  if (filter.filter_type === 'boolean') {
    return {
      key,
      name: filter.name_translated,
      text: t(filters[key] === 'true' ? i18nKeys.YES : i18nKeys.NO),
    };
  }
  if (filter.filter_type === 'number' && filters[key] != null) {
    const operator = filters[`${key}${OPERATOR_SUFFIX}`];
    return {
      key,
      pairedKey: `${key}${OPERATOR_SUFFIX}`,
      name: filter.name_translated,
      text: isNullish(operator) ? `${filters[key]}` : `${operator} ${filters[key]}`,
    };
  }
  if (filter.filter_type === 'select' || filter.filter_type === 'string') {
    return { key, name: filter.name_translated, text: valueText(key, filter, ctx) };
  }
  return null;
};

const chipForKey = (key: string, ctx: ChipContext): DisplayFilter | null => {
  if (ctx.filters[key] == null || BOOKKEEPING_KEYS.includes(key)) return null;
  if (key.includes(RANGE_START_SUFFIX) || key.includes(RANGE_END_SUFFIX))
    return dateRangeChip(key, ctx);
  if (key.includes(HANDLER_SUFFIX)) return handlerChip(key, ctx);
  if (isMinMaxKey(key)) return minMaxChip(key, ctx);
  if (key === 'quick_search') return quickSearchChip(key, ctx);
  if (!key.includes(OPERATOR_SUFFIX)) return valueChip(key, ctx);
  return null;
};

/** Build the active filter "chips" shown above the table. `t` is injected to stay pure. */
export const buildVisibleFilters = (options: BuildVisibleFiltersOptions): DisplayFilter[] => {
  if (options.availableFilters.length === 0) return [];

  const chips = Object.keys(options.filters)
    .map(key => chipForKey(key, options))
    .filter(isNonNullish);

  // Drop the second half of a start/end or min/max pair: its partner emitted first.
  const claimedAsPartner = new Set<string>();
  return chips.filter(chip => {
    const isSecondHalf = claimedAsPartner.has(chip.key);
    if (chip.pairedKey) claimedAsPartner.add(chip.pairedKey);
    return !isSecondHalf;
  });
};
